import React, { useState, useContext } from 'react';
import { Button, Tooltip, message, Modal, List } from 'antd';
import {
  AiOutlineDownload,
  AiOutlineWarning,
  AiOutlineCheck,
  AiOutlineClose,
} from 'react-icons/ai';
import LocaleContext from 'locales';
import { getRunningProcesses } from 'services/vsclient';

// 필수 보안 프로그램 목록
const REQUIRED_PROGRAMS = {
  forcepoint: ['wepsvc.exe', 'Dserui.exe', 'EndPointClassifier.exe', 'kvoop.exe', 'F1EUI.exe'],
  printchaser: ['Proxtrac.exe'],
};

// 프로세스명 대소문자 무시하고 비교
const isProcessRunning = (processName, runningProcesses) => {
  const lowerProcessName = processName.toLowerCase();
  return runningProcesses.some((p) => p.toLowerCase() === lowerProcessName);
};

// 보안 프로그램 실행 상태를 표시하는 컴포넌트
const SecurityProgramList = ({ title, programs, runningPrograms, locale }) => (
  <div>
    <h4 style={{ margin: '16px 0 8px 0' }}>{title}</h4>
    <List
      bordered
      dataSource={programs}
      renderItem={(program) => {
        const isRunning = runningPrograms.includes(program);
        return (
          <List.Item>
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginRight: '8px' }}>
                {isRunning ? (
                  <AiOutlineCheck style={{ color: '#52c41a', fontSize: '16px' }} />
                ) : (
                  <AiOutlineClose style={{ color: '#ff4d4f', fontSize: '16px' }} />
                )}
              </div>
              <div style={{ flex: 1 }}>
                <span>{program}</span>
              </div>
              <div>
                {isRunning ? (
                  <span style={{ color: '#52c41a' }}>
                    {locale.resource['txt.file-transfer.running']}
                  </span>
                ) : (
                  <span style={{ color: '#ff4d4f' }}>
                    {locale.resource['txt.file-transfer.required']}
                  </span>
                )}
              </div>
            </div>
          </List.Item>
        );
      }}
    />
  </div>
);

// 보안 요구사항 모달 컴포넌트
const SecurityRequirementsModal = ({
  visible,
  onClose,
  onRetry,
  isExternalAccess,
  runningPrograms,
  locale,
}) => (
  <Modal
    title={
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <AiOutlineWarning style={{ color: '#faad14', marginRight: '8px', fontSize: '20px' }} />
        <span>{locale.resource['txt.file-transfer.security-requirements']}</span>
      </div>
    }
    visible={visible}
    onCancel={onClose}
    footer={[
      <Button key="close" onClick={onClose}>
        {locale.resource['txt.file-transfer.close']}
      </Button>,
      // 내부망 접속인 경우에만 "다시 시도" 버튼 표시
      ...(!isExternalAccess
        ? [
            <Button key="retry" type="primary" onClick={onRetry}>
              {locale.resource['txt.file-transfer.retry']}
            </Button>,
          ]
        : []),
    ]}
    width={600}
  >
    {isExternalAccess ? (
      // 외부망 접속인 경우 UI
      <div>
        <p>{locale.resource['txt.file-transfer.external-access-message']}</p>
        <h4 style={{ margin: '16px 0 8px 0' }}>
          {locale.resource['txt.file-transfer.access-location']}
        </h4>
        <List
          bordered
          dataSource={[locale.resource['txt.file-transfer.external-network']]}
          renderItem={(item) => (
            <List.Item>
              <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <div style={{ marginRight: '8px' }}>
                  <AiOutlineClose style={{ color: '#ff4d4f', fontSize: '16px' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <span>{item}</span>
                </div>
                <div>
                  <span style={{ color: '#ff4d4f' }}>
                    {locale.resource['txt.file-transfer.download-not-allowed']}
                  </span>
                </div>
              </div>
            </List.Item>
          )}
        />
      </div>
    ) : (
      // 내부망 접속인 경우 보안 프로그램 실행 상태 표시
      <div style={{ marginBottom: '20px' }}>
        <p style={{ whiteSpace: 'pre-line' }}>
          {locale.resource['txt.file-transfer.security-instruction']?.replace('{break}', '\n')}
        </p>

        {/* 보안 프로그램 그룹별로 표시 */}
        <SecurityProgramList
          title="Forcepoint One Endpoint"
          programs={REQUIRED_PROGRAMS.forcepoint}
          runningPrograms={runningPrograms}
          locale={locale}
        />

        <SecurityProgramList
          title={locale.resource['txt.file-transfer.printchaser-title'] || 'PrintChaser'}
          programs={REQUIRED_PROGRAMS.printchaser}
          runningPrograms={runningPrograms}
          locale={locale}
        />
      </div>
    )}
  </Modal>
);

// 다운로드 버튼 컴포넌트
const Download = ({ selectedRowKeys, onDownload }) => {
  const [isCheckingSecurity, setIsCheckingSecurity] = useState(false);
  const [runningPrograms, setRunningPrograms] = useState([]);
  const [securityModalVisible, setSecurityModalVisible] = useState(false);
  const [isExternalAccess, setIsExternalAccess] = useState(false);
  const { locale } = useContext(LocaleContext);

  // 보안 프로그램 실행 여부 확인
  const checkRequiredSecurityPrograms = async () => {
    try {
      const response = await getRunningProcesses();

      if (!response?.data?.success || !Array.isArray(response.data.processNames)) {
        throw new Error(response?.data?.reason?.error);
      }

      const runningProcesses = response.data.processNames;
      const allRequiredPrograms = [
        ...REQUIRED_PROGRAMS.forcepoint,
        ...REQUIRED_PROGRAMS.printchaser,
      ];

      const missingProgramsList = allRequiredPrograms.filter(
        (program) => !isProcessRunning(program, runningProcesses),
      );

      const runningRequiredPrograms = allRequiredPrograms.filter((program) =>
        isProcessRunning(program, runningProcesses),
      );

      return {
        success: true,
        securityCheckPassed: missingProgramsList.length === 0,
        runningPrograms: runningRequiredPrograms,
      };
    } catch (error) {
      console.error('필수 보안 프로그램 확인 오류:', error);
      return {
        success: false,
        error: 'security-check-error',
        message: locale.resource['txt.file-transfer.security-check-failed'],
      };
    }
  };

  // 내부망 접속 여부 확인
  const isInternalAccess = () => {
    const isExternal = sessionStorage.getItem('isExternalAccess');
    return isExternal !== 'true';
  };

  const checkDownloadPermission = async () => {
    try {
      // 1. 내부망 접속 여부 확인
      const isInternal = isInternalAccess();

      if (!isInternal) {
        return {
          success: true,
          isAllowed: false,
          reason: 'external-access',
          message: locale.resource['txt.file-transfer.external-access-message'],
        };
      }

      // 2. 보안 프로그램 실행 여부 확인
      const securityResult = await checkRequiredSecurityPrograms();

      // 보안 체크 자체가 실패한 경우
      if (!securityResult.success) {
        return {
          success: false,
          isAllowed: false,
          reason: securityResult.error || 'security-check-failed',
          message: securityResult.message,
        };
      }

      // 3. 모든 보안 프로그램 실행 여부에 따라 다운로드 허용 결정
      const isAllowed = securityResult.securityCheckPassed;

      return {
        success: true,
        isAllowed,
        reason: isAllowed ? 'all-requirements-met' : 'missing-security-programs',
        missingPrograms: securityResult.missingPrograms || [],
        runningPrograms: securityResult.runningPrograms || [],
      };
    } catch (error) {
      console.error('다운로드 권한 확인 오류:', error);
      return {
        success: false,
        isAllowed: false,
        reason: 'permission-check-error',
      };
    }
  };

  // 다운로드 핸들러
  const handleDownload = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning(locale.resource['txt.file-transfer.msg-select-files']);
      return;
    }

    // 보안 프로그램 실행 여부 확인 시작
    setIsCheckingSecurity(true);

    try {
      // 엔솔 보안 요구사항 체크
      const securityResult = await checkDownloadPermission();

      if (!securityResult.success) {
        // 체크 자체가 실패한 경우
        message.error(securityResult.message);
        return;
      }

      // 외부망 접속 여부 저장
      setIsExternalAccess(securityResult.reason === 'external-access');

      if (!securityResult.isAllowed) {
        // 다운로드 불가능한 경우 (외부망 또는 보안 프로그램 미실행)
        setRunningPrograms(securityResult.runningPrograms || []);
        setSecurityModalVisible(true);
        return;
      }

      // 모든 조건 충족 - 다운로드 진행
      onDownload();
    } catch (error) {
      console.error('다운로드 권한 확인 오류:', error);
    } finally {
      setIsCheckingSecurity(false);
    }
  };

  // 모달 재시도 핸들러
  const handleRetry = async () => {
    setSecurityModalVisible(false);
    // 약간의 지연 후 다시 다운로드 로직 실행
    setTimeout(() => {
      handleDownload();
    }, 100);
  };

  return (
    <>
      <Tooltip
        placement="topRight"
        title={
          selectedRowKeys.length === 0
            ? locale.resource['txt.file-transfer.msg-select-files']
            : null
        }
      >
        <Button
          type="text"
          icon={<AiOutlineDownload className="anticon" />}
          onClick={handleDownload}
          disabled={selectedRowKeys.length === 0}
          loading={isCheckingSecurity}
        >
          {locale.resource['txt.file-transfer.download']}
        </Button>
      </Tooltip>

      <SecurityRequirementsModal
        visible={securityModalVisible}
        onClose={() => setSecurityModalVisible(false)}
        onRetry={handleRetry}
        isExternalAccess={isExternalAccess}
        runningPrograms={runningPrograms}
        locale={locale}
      />
    </>
  );
};

export default Download;
